(() => {
  'use strict';

  if (customElements.get('mdd-wholesale-savings')) return;

  const money = value =>
    new Intl.NumberFormat(
      document.documentElement.lang || 'en-US',
      {
        style: 'currency',
        currency: 'USD'
      }
    ).format(value / 100);

  class Savings extends HTMLElement {
    connectedCallback() {
      try {
        const state = this.querySelector(
          '[data-savings-state]'
        );

        this.render(JSON.parse(state.textContent));
      } catch (_) {
        this.unavailable();
      }
    }

    unavailable() {
      const output = this.querySelector(
        '[data-savings-output]'
      );

      if (output) {
        output.textContent =
          'Eligible savings are confirmed at checkout. ' +
          'Refresh to check current progress.';
      }
    }

    render(cart) {
      const output = this.querySelector(
        '[data-savings-output]'
      );

      if (!output) return;

      const state = window.MddWholesaleSavingsModel(cart);
      const fragment = document.createDocumentFragment();

      const line = text => {
        const paragraph = document.createElement('p');
        paragraph.textContent = text;
        fragment.append(paragraph);
      };

      if (state.unknown) {
        line(
          'USD wholesale thresholds are shown on product pages. ' +
          'Eligibility and savings for this cart are confirmed ' +
          'at checkout.'
        );
      } else {
        if (state.applied.length) {
          state.applied.forEach(discount => {
            line(
              money(discount.total_allocated_amount) +
              ' wholesale savings applied.'
            );
          });
        } else if (state.unlocked) {
          line(
            "You've unlocked " +
            money(state.unlocked.off) +
            ' OFF eligibility. Shopify has not yet confirmed ' +
            'this discount as applied.'
          );
        }

        if (state.next) {
          line(
            'Add ' +
            money(state.remaining) +
            ' more in eligible items to unlock ' +
            money(state.next.off) +
            ' OFF.'
          );
        } else {
          line(
            "You've reached the highest wholesale savings tier."
          );
        }

        const bar = document.createElement('progress');

        bar.max = 80000;
        bar.value = Math.min(state.subtotal, 80000);

        bar.setAttribute(
          'aria-label',
          'Eligible subtotal progress toward USD $800'
        );

        fragment.append(bar);

        line(
          'Eligible subtotal: ' +
          money(state.subtotal) +
          '. After product discounts, before order savings.'
        );
      }

      output.replaceChildren(fragment);
    }
  }

  customElements.define('mdd-wholesale-savings', Savings);

  let timer;
  let controller;
  let revision = 0;

  function schedule() {
    const version = ++revision;

    clearTimeout(timer);

    if (controller) controller.abort();

    document
      .querySelectorAll('mdd-wholesale-savings')
      .forEach(element => element.unavailable());

    timer = setTimeout(async () => {
      if (!document.querySelector('mdd-wholesale-savings')) {
        return;
      }

      controller = new AbortController();

      try {
        const root = window.Shopify?.routes?.root || '/';

        const response = await fetch(root + 'cart.js', {
          credentials: 'same-origin',
          cache: 'no-store',
          signal: controller.signal
        });

        if (!response.ok) {
          throw new Error('Cart read failed');
        }

        const cart = await response.json();

        cart.unknownEligibility =
          !Array.isArray(cart.items) ||
          cart.items.some(item => item.gift_card);

        if (version !== revision) return;

        document
          .querySelectorAll('mdd-wholesale-savings')
          .forEach(element => element.render(cart));
      } catch (error) {
        if (
          error.name !== 'AbortError' &&
          version === revision
        ) {
          document
            .querySelectorAll('mdd-wholesale-savings')
            .forEach(element => element.unavailable());
        }
      }
    }, 120);
  }

  // Reuse Refresh's native cart update event.
  if (
    typeof subscribe === 'function' &&
    typeof PUB_SUB_EVENTS !== 'undefined'
  ) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
  }

  // Observe completed cart requests without changing them.
  if (typeof PerformanceObserver !== 'undefined') {
    try {
      const observer = new PerformanceObserver(list => {
        const cartChanged = list.getEntries().some(entry => {
          const url = new URL(entry.name, location.href);

          return (
            url.origin === location.origin &&
            /\/cart\/(add|change|update|clear)(\.js)?$/.test(
              url.pathname
            )
          );
        });

        if (cartChanged) schedule();
      });

      observer.observe({
        entryTypes: ['resource']
      });
    } catch (_) {
      // The native cart-update event remains available.
    }
  }

  document.addEventListener(
    'shopify:section:load',
    schedule
  );

  window.addEventListener('pageshow', schedule);
})();